/* StatementImporter — standalone page for OCR-based statement import.
 *
 * Re-houses the Document Scanner (formerly a tab inside CFO Buck) under
 * the Accounting Hub. The actual scanning component is shared with CFO Buck's
 * tax-return analysis flow — when a user uploads a tax return here, we offer
 * a deep-link back into CFO Buck to run the tax analysis.
 */
import { useCallback, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { ScannerTab } from '../components/cfobuck/ScannerTab';
import { ScanLine } from 'lucide-react';
import { fmt } from '../components/cfobuck/constants';
import { TooltipProvider } from '../components/ui/tooltip';

export default function StatementImporter() {
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [pendingTaxData, setPendingTaxData] = useState(null);

  // If user lands here via a deep-link from CFO Buck (?docType=tax_return),
  // the ScannerTab default doc type is pre-set in localStorage so it opens
  // pre-configured for tax return parsing.
  useEffect(() => {
    const docType = searchParams.get('docType');
    if (docType === 'tax_return') {
      try { localStorage.setItem('scanner_default_doc_type', 'tax_return'); } catch { /* ignore */ }
    }
  }, [searchParams]);

  // When a tax return is scanned, hand the structured data back to CFO Buck.
  const handleAnalyzeTaxReturn = useCallback((taxData) => {
    setPendingTaxData(taxData);
    // Stash in sessionStorage so CFOBuck picks it up on mount
    const parts = [];
    if (taxData.tax_year) parts.push(`Tax Year: ${taxData.tax_year}`);
    if (taxData.filing_status) parts.push(`Filing Status: ${taxData.filing_status}`);
    if (taxData.income?.agi != null) parts.push(`AGI: ${fmt(taxData.income.agi)}`);
    if (taxData.income?.wages != null) parts.push(`W-2 Wages: ${fmt(taxData.income.wages)}`);
    if (taxData.income?.business != null) parts.push(`Business Income: ${fmt(taxData.income.business)}`);
    if (taxData.deductions?.type) parts.push(`Deduction Type: ${taxData.deductions.type} (${fmt(taxData.deductions.amount)})`);
    if (taxData.taxable_income != null) parts.push(`Taxable Income: ${fmt(taxData.taxable_income)}`);
    if (taxData.tax_liability != null) parts.push(`Tax Liability: ${fmt(taxData.tax_liability)}`);
    if (taxData.refund_or_owed != null) parts.push(`${taxData.refund_or_owed >= 0 ? 'Refund' : 'Owed'}: ${fmt(taxData.refund_or_owed)}`);

    const prompt = `I just uploaded my ${taxData.tax_year || 'prior year'} tax return. Here's the extracted data:\n\n${parts.join('\n')}\n\nPlease analyze this return for:\n1. Deductions I may have missed\n2. Credits I could have claimed\n3. Filing status optimization opportunities\n4. Strategies to reduce my tax liability next year\n5. Any red flags or areas of concern`;

    try { sessionStorage.setItem('cfo_pending_tax_analysis', prompt); } catch { /* ignore */ }
    navigate('/cfo-buck?tab=chat');
  }, [navigate]);

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-6" data-testid="statement-importer-page">
        <div className="flex items-end justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <ScanLine className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-foreground" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="statement-importer-heading">
                Document OCR Scanner
              </h1>
              <p className="text-sm text-muted-foreground">
                Upload bank statements, receipts, invoices, or tax returns &mdash; AI extracts the data for you
              </p>
            </div>
          </div>
        </div>

        <ScannerTab workspaceId={wsId} onAnalyzeTaxReturn={handleAnalyzeTaxReturn} />

        {pendingTaxData && (
          <div className="hidden" aria-hidden="true">Redirecting to CFO Buck for analysis...</div>
        )}
      </div>
    </TooltipProvider>
  );
}
